import { TileDefinition } from '../types/dashboard';
import { ensureNumber } from './utils';

export type TileDisplayContext = 'preview' | 'dashboard' | 'leader';

export function getContextDisplayName(context: TileDisplayContext): string {
  switch (context) {
    case 'preview':
      return 'Preview';
    case 'dashboard':
      return 'Dashboard';
    case 'leader':
      return 'Leader';
    default:
      return 'Preview';
  }
}

export function getContextDescription(context: TileDisplayContext): string {
  switch (context) {
    case 'preview':
      return 'Preview context shows the chart in the Tile Builder editing canvas.';
    case 'dashboard':
      return 'Dashboard context shows how the tile renders in a dashboard card.';
    case 'leader':
      return 'Leader context shows how the tile renders inside Leader pages and strips.';
    default:
      return '';
  }
}

type MarginSettings = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

function normalizeMargins(input: any, fallback: MarginSettings): MarginSettings {
  return {
    top: ensureNumber(input?.top, fallback.top),
    right: ensureNumber(input?.right, fallback.right),
    bottom: ensureNumber(input?.bottom, fallback.bottom),
    left: ensureNumber(input?.left, fallback.left),
  };
}

function getLeaderMarginsFromSettings(settings: any, legendShown: boolean): MarginSettings {
  const fallback: MarginSettings = { top: 24, right: 16, bottom: 48, left: 16 };

  const explicit = legendShown ? settings?.leaderLegendMargins : settings?.leaderBaseMargins;
  if (explicit && typeof explicit === 'object') {
    return normalizeMargins(explicit, fallback);
  }

  // backward compatibility fallback
  const legacy = legendShown ? settings?.legendMargins : settings?.baseMargins;
  if (legacy && typeof legacy === 'object') {
    return normalizeMargins(legacy, fallback);
  }

  return normalizeMargins(
    {
      top: settings?.marginTop,
      right: settings?.marginRight,
      bottom: settings?.marginBottom,
      left: settings?.marginLeft,
    },
    fallback
  );
}

export function normalizeLeaderTileSettings(tile: TileDefinition): TileDefinition {
  const settings = { ...(tile?.settings ?? {}) };
  const tileLevelShowLegend =
    tile?.show_legend === true
      ? true
      : tile?.show_legend === false
        ? false
        : settings?.show_legend === true;

  const leaderMargins = getLeaderMarginsFromSettings(settings, tileLevelShowLegend);

  return {
    ...tile,
    show_legend: tileLevelShowLegend,
    settings: {
      ...settings,
      show_legend: tileLevelShowLegend,

      // mirror active leader preset into flat margins because many chart renderers still read these
      marginTop: leaderMargins.top,
      marginRight: leaderMargins.right,
      marginBottom: leaderMargins.bottom,
      marginLeft: leaderMargins.left,
    },
  };
}